import React from 'react';
import type { TeamResponse } from '../types';
import { Avatar, AvatarFallback } from '@/shared/components/ui/avatar';
import { MemberAvatarList, StatusPill, Switch } from '@/shared/components/ui';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { useUsersDropdown } from '@/modules/users/hooks/useUsers';
import { useAddTeamUser, useRemoveTeamUser } from '../hooks';
import { Icon } from '@/shared/icons';

/**
 * Linha de tabela de equipe (view "Lista"). Padrão-lista travado: checkbox de
 * seleção (1ª col) + célula multi-linha (nome + descrição) + menu ••• (última
 * col). Avatar da equipe = slate (#64748b via AvatarFallback) com glyph "users".
 * A gestão inline de membros é preservada pelo MemberAvatarList.
 */
export interface TeamListItemProps {
  /** Team data from API */
  team: TeamResponse;
  /** Members assigned to this team */
  members?: Array<{ id: string; name: string; avatar?: string }>;
  /** Whether this row is selected (bulk selection) */
  selected?: boolean;
  /** Callback to toggle selection */
  onToggleSelect?: (id: string) => void;
  /** Callback when edit is clicked */
  onEdit?: (id: string) => void;
  /** Callback when delete is clicked */
  onDelete?: (id: string) => void;
  /** Callback when activate/deactivate is clicked */
  onToggleStatus?: (id: string, currentStatus: string) => void;
  /** Atalho para gerenciar membros (navega pro form da equipe) */
  onManageMembers?: (id: string) => void;
}

export const TeamListItem: React.FC<TeamListItemProps> = ({
  team,
  members = [],
  selected = false,
  onToggleSelect,
  onEdit,
  onDelete,
  onToggleStatus,
  onManageMembers,
}) => {
  // Local state for instant updates (optimistic member add/remove)
  const [localMembers, setLocalMembers] = React.useState(members);
  React.useEffect(() => {
    setLocalMembers(members);
  }, [members]);

  // Fetch available users for the add-member dropdown
  const { data: allUsers = [], isLoading: loadingUsers } = useUsersDropdown({ status: 'active' });
  const availableUsers = allUsers;

  const addUserMutation = useAddTeamUser();
  const removeUserMutation = useRemoveTeamUser();

  const handleAddMember = (userId: string) => {
    const newMember = availableUsers.find((u) => u.id === userId);
    if (newMember) setLocalMembers([...localMembers, newMember]);
    addUserMutation.mutate({ teamId: team.id, userId });
  };

  const handleRemoveMember = (userId: string) => {
    setLocalMembers(localMembers.filter((m) => m.id !== userId));
    removeUserMutation.mutate({ teamId: team.id, userId });
  };

  const isActive = team.status === 'active';

  const actions: CardAction[] = [];
  if (onEdit) actions.push({ label: 'Editar', icon: 'edit', onClick: () => onEdit(team.id) });
  if (onManageMembers) actions.push({ label: 'Gerenciar membros', icon: 'users', onClick: () => onManageMembers(team.id) });
  if (onToggleStatus) {
    actions.push({
      label: isActive ? 'Desativar' : 'Ativar',
      icon: 'power',
      onClick: () => onToggleStatus(team.id, team.status),
    });
  }
  if (onDelete) actions.push({ label: 'Excluir', icon: 'trash', onClick: () => onDelete(team.id), variant: 'danger', dividerBefore: true });

  return (
    <tr
      className="border-b transition-colors"
      style={{ borderColor: 'var(--theme-card-border)', backgroundColor: selected ? 'var(--theme-primary-background)' : undefined }}
      onMouseEnter={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
      onMouseLeave={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'transparent'; }}
      data-testid={`team-row-${team.id}`}
    >
      {/* Checkbox */}
      <td className="pl-4 pr-1 py-3 w-11">
        <button
          type="button"
          onClick={() => onToggleSelect?.(team.id)}
          className="w-[18px] h-[18px] rounded-[5px] border-[1.6px] flex items-center justify-center transition-colors"
          style={{
            backgroundColor: selected ? 'var(--theme-primary)' : 'transparent',
            borderColor: selected ? 'var(--theme-primary)' : 'var(--theme-input-border)',
            color: '#ffffff',
          }}
          aria-label={selected ? 'Desmarcar' : 'Selecionar'}
          data-testid={`team-check-${team.id}`}
        >
          {selected && <Icon name="check" className="w-3 h-3" />}
        </button>
      </td>

      {/* Equipe: avatar + nome */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <Avatar className="w-9 h-9 flex-shrink-0" isSquircle>
            <AvatarFallback isSquircle>
              <Icon name="users" className="w-4 h-4 text-white" />
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="font-semibold text-sm truncate" style={{ color: 'var(--theme-card-text)' }}>
              {team.name}
            </div>
          </div>
        </div>
      </td>

      {/* Descrição */}
      <td className="px-4 py-3">
        <div className="text-sm truncate max-w-xs" style={{ color: 'var(--theme-muted-text)' }}>
          {team.description || 'Sem descrição'}
        </div>
      </td>

      {/* Membros */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <MemberAvatarList
            members={localMembers}
            availableUsers={availableUsers}
            limit={3}
            onAddMember={handleAddMember}
            onRemoveMember={handleRemoveMember}
            canRemoveMembers={false}
            loading={loadingUsers}
            placeholder="Buscar membro..."
            emptyMessage="Sem membros"
          />
          <span className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>{localMembers.length}</span>
        </div>
      </td>

      {/* Status — F47: switch inline (Switch shared sm); pill só quando não togglável (F62) */}
      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
        {onToggleStatus ? (
          <div className="inline-flex items-center gap-2" data-testid={`team-toggle-${team.id}`}>
            <Switch size="sm" checked={isActive} onChange={() => onToggleStatus(team.id, team.status)} />
            <span className="text-xs font-semibold" style={{ color: isActive ? 'var(--theme-success-fg)' : 'var(--theme-muted-text)' }}>
              {isActive ? 'Ativa' : 'Inativa'}
            </span>
          </div>
        ) : (
          <StatusPill
            variant={isActive ? 'success' : 'neutral'}
            label={isActive ? 'Ativa' : 'Inativa'}
            data-testid={`team-status-${team.id}`}
          />
        )}
      </td>

      {/* Ações */}
      <td className="px-4 py-3 text-right w-14">
        <CardActionsMenu actions={actions} size="sm" data-testid={`team-row-actions-${team.id}`} />
      </td>
    </tr>
  );
};

TeamListItem.displayName = 'TeamListItem';
